import { Check, ChevronDown, Copy, LogOut, Plus, Settings } from 'lucide-react'
import type { AccountDto } from '../../data/types'
import { shortAddress } from '../../core/format'
import { formatUsdBalance, formatUsdBalanceCompact } from '../../core/pricing'
import { WALLET_LABEL, type Connection } from '../../core/wallet'
import { Copyable } from '../../ui/Formatting'
import { Avatar } from '../../ui/LayoutPrimitives'
import { useDisclosure } from './useDisclosure'

const PANEL_ID = 'account-menu-panel'

/** The wallet balance as the bar and the menu both show it: dollars, never a denom. */
export function walletBalanceText(account: AccountDto | null): { display: string; exact: string | undefined } {
  if (!account) return { display: '…', exact: undefined }
  const exact = formatUsdBalance(account.balance) ?? undefined
  return { display: formatUsdBalanceCompact(account.balance) ?? 'Balance unavailable', exact }
}

interface Props {
  connection: Connection
  account: AccountDto | null
  walletName: string
  onDisconnect: () => void
  onNavigate?: ((path: string) => void) | undefined
}

/**
 * The account disclosure: who is connected, what the wallet holds, and the way
 * out.
 *
 * Billing has no funding anchor of its own, so "Add funds" is the only row that
 * goes there — a separate "Billing" row would be a second name for the same
 * destination.
 */
export function AccountMenu({ connection, account, walletName, onDisconnect, onNavigate }: Props) {
  const { open, toggle, close, triggerRef, panelRef, onBlur } = useDisclosure<HTMLButtonElement, HTMLDivElement>()
  const balance = walletBalanceText(account)
  const short = shortAddress(connection.address)
  // Two different names. `walletName` is the key's label from the extension
  // ("Main", "work"), which identifies the account. The permission belongs to
  // the extension itself, so every sentence about the extension names it.
  const extension = WALLET_LABEL[connection.wallet]
  const keyName = walletName !== extension ? walletName : null
  // Every row that leaves the panel closes it first, so the panel never
  // outlives the page it was opened on.
  const go = (path: string) => { close(); onNavigate?.(path) }

  return (
    <div className="account-menu">
      <button
        ref={triggerRef}
        type="button"
        className="account-menu-trigger"
        aria-label="Open account menu"
        aria-expanded={open}
        aria-controls={PANEL_ID}
        onClick={toggle}
        onBlur={onBlur}
      >
        <Avatar name={walletName} />
        <code className="account-menu-address">{short}</code>
        <ChevronDown className="account-menu-chevron" size={16} aria-hidden="true" />
      </button>
      <div ref={panelRef} id={PANEL_ID} className="account-menu-panel" role="region" aria-label="Account" hidden={!open} onBlur={onBlur}>
        <div className="account-menu-section account-menu-identity">
          <span className="account-menu-avatar"><Avatar name={walletName} /></span>
          <div className="account-menu-who">
            <p className="account-menu-name"><strong>{extension} wallet</strong><span className="account-menu-status"><span className="dot dot-ok" aria-hidden="true" />Connected</span></p>
            {keyName && <span className="account-menu-key-name">{keyName}</span>}
            <Copyable
              value={connection.address}
              display={<code className="account-menu-full-address" title={connection.address}>{short}</code>}
              buttonLabel="Copy address"
              icon={<Copy size={14} aria-hidden="true" />}
              copiedIcon={<Check size={14} aria-hidden="true" />}
            />
          </div>
        </div>
        <div className="account-menu-section account-menu-balance">
          <span>Wallet balance</span>
          <span className="account-menu-balance-value" title={balance.exact}>{balance.display}</span>
        </div>
        {onNavigate && (
          <div className="account-menu-section">
            <button type="button" className="account-menu-item" onClick={() => go('/app/billing')}><Plus size={16} aria-hidden="true" />Add funds</button>
            <button type="button" className="account-menu-item" onClick={() => go('/app/settings')}><Settings size={16} aria-hidden="true" />Account settings</button>
          </div>
        )}
        <div className="account-menu-section">
          <button type="button" className="account-menu-item account-menu-item--danger" onClick={() => { close(); onDisconnect() }}><LogOut size={16} aria-hidden="true" />Disconnect</button>
          <p className="account-menu-session-note">Signs out of this console only. {extension} keeps its permission until you revoke it there.</p>
        </div>
      </div>
    </div>
  )
}
